import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Alert, Button, Empty, Popconfirm, Select, Table, Tag, message,
} from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import AddNextgenTaskTemplate from '../../modal/AddNextgenTaskTemplate';
import csrfHeaders from '../../../utils/csrfHeaders';
import { captureAnalytics } from '../../../actions/preload';
import './manage-nextgen-task-templates.scss';

const BASE = '/api/v1/nextgen_task_templates';
// Users only ever see their own personal templates, so a User owner is "Only me".
const OWNER_LABELS = { Organization: 'Entire organization', User: 'Only me' };
const ownerLabel = (template) => OWNER_LABELS[template.owner_type] || `${template.owner_type}: ${template.owner_name}`;

// Settings → Task Templates for organizations whose NextGen connection has
// tasking enabled. `context` comes from GET /api/v1/nextgen_task_templates/context.
const ManageNextgenTaskTemplates = ({ context }) => {
  const { credentials, owner_options: ownerOptions } = context;
  const [credentialId, setCredentialId] = useState(credentials[0].id);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // { template, credentialId }: the editor stays on the connection it was opened for.
  const [editing, setEditing] = useState(null);
  // Only the latest list request may update the page, so a slow reply for a
  // connection the user has left never replaces the current one's templates.
  const latestRequestRef = useRef(0);

  const load = useCallback(async () => {
    latestRequestRef.current += 1;
    const request = latestRequestRef.current;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(BASE, { params: { emr_integration_credential_id: credentialId } });
      if (request === latestRequestRef.current) setTemplates(response.data.nextgen_task_templates);
    } catch (err) {
      if (request === latestRequestRef.current) {
        setError(err.response?.data?.error || 'Could not load task templates. Please try again.');
      }
    } finally {
      if (request === latestRequestRef.current) setLoading(false);
    }
  }, [credentialId]);

  useEffect(() => { load(); }, [load]);

  const openEditor = (template) => {
    captureAnalytics('nextgen_task_template_editor_opened', { mode: template.id ? 'edit' : 'add' });
    setEditing({ template, credentialId: template.emr_integration_credential_id || credentialId });
  };

  const remove = async (template) => {
    try {
      await axios.delete(`${BASE}/${template.id}`, { headers: csrfHeaders() });
      captureAnalytics('nextgen_task_template_deleted', { owner_type: template.owner_type });
      message.success('Task template deleted');
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Could not delete the task template. Please try again.');
    }
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Document types',
      key: 'document_types',
      render: (_, template) => template.document_types.map((type) => (
        <Tag key={type.id}>{type.label || 'Unavailable type'}</Tag>
      )),
    },
    { title: 'Category', key: 'category', render: (_, template) => template.category_label || '—' },
    {
      title: 'Priority', key: 'priority', render: (_, template) => template.priority_label || template.priority,
    },
    { title: 'Subject', key: 'subject', render: (_, template) => template.subject || 'Document title' },
    {
      title: 'Due', key: 'due', render: (_, template) => (template.due_in_days == null ? '—' : `${template.due_in_days} days`),
    },
    {
      title: 'Fields', key: 'fields', render: (_, template) => template.fields.map((field) => field.label).join(', ') || '—',
    },
    { title: 'Available to', key: 'owner', render: (_, template) => ownerLabel(template) },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, template) => (
        <>
          <Button size="small" onClick={() => openEditor(template)} style={{ marginRight: 8 }}>Edit</Button>
          <Popconfirm title="Delete this template? This cannot be undone." onConfirm={() => remove(template)}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div className="manage-nextgen-task-templates">
      <div className="manage-nextgen-task-templates__header">
        <h2>NextGen Task Templates</h2>
        <p>
          Save the task details your team uses for each NextGen document type. A template loads in
          Send to Chart when one of its document types is picked.
        </p>
      </div>
      <div className="manage-nextgen-task-templates__toolbar">
        <Button
          className="task-templates-add-button"
          icon={<PlusCircleOutlined />}
          type="primary"
          onClick={() => openEditor({})}
        >
          Add Task Template
        </Button>
        {credentials.length > 1 && (
          <Select
            aria-label="NextGen connection"
            value={credentialId}
            style={{ width: 220 }}
            onChange={(value) => {
              captureAnalytics('nextgen_task_templates_connection_changed');
              setTemplates([]);
              setCredentialId(value);
            }}
            options={credentials.map((credential) => ({ value: credential.id, label: credential.name }))}
          />
        )}
      </div>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <Table
        size="small"
        bordered
        loading={loading}
        rowKey="id"
        dataSource={templates}
        columns={columns}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No task templates yet" /> }}
        pagination={{ position: ['bottomCenter'], pageSize: 20 }}
      />
      {editing && (
        <AddNextgenTaskTemplate
          credentialId={editing.credentialId}
          ownerOptions={ownerOptions}
          template={editing.template}
          onClose={() => setEditing(null)}
          onSaved={() => {
            message.success(editing.template.id ? 'Task template updated' : 'Task template added');
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
};

ManageNextgenTaskTemplates.propTypes = {
  context: PropTypes.shape({
    credentials: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })).isRequired,
    owner_options: PropTypes.array.isRequired,
  }).isRequired,
};

export default ManageNextgenTaskTemplates;
